// §7 — virtual filesystem types and the path grammar's constants.
//
// The grammar itself (parse/normalise/confine) lives in `kernel/vfs/confine.ts` (§7.1); this
// module owns only the shapes both sides of the bridge must agree on.

/** Absolute, POSIX, NFC-normalised. See §7.1. */
export type VfsPath = string;

/** §7.1 — max segment 255 bytes, max path 4096 bytes. */
export const MAX_SEGMENT_BYTES = 255;
export const MAX_PATH_BYTES = 4096;

/** The three roots of §7.1. */
export const ROOT_USERS = '/Users';
export const ROOT_APPS = '/Apps';
export const ROOT_SYSTEM = '/System';

/** The user-space folders bootstrapped for every session (§7.1). */
export const USER_FOLDERS = ['Desktop', 'Documents', 'Downloads', 'Pictures'] as const;
export type UserFolder = (typeof USER_FOLDERS)[number];

/** The trash directory name inside a user's space (§7.8). */
export const TRASH_DIR = '.Trash';

export interface FileEntry {
  path: VfsPath;
  name: string;
  isDir: boolean;
  size: number;
  /** Epoch milliseconds. */
  mtime: number;
  /** Server-assigned monotonic revision; 0 for never-synced local entries (§7.4). */
  rev: number;
  /** SHA-256 of the bytes, when known (D-047). */
  sha?: string;
}

export interface VersionEntry {
  id: string;
  path: VfsPath;
  rev: number;
  size: number;
  /** Epoch milliseconds. */
  createdAt: number;
  sha: string;
}

export type FsEventKind = 'create' | 'modify' | 'delete' | 'move';

export interface FsEvent {
  kind: FsEventKind;
  path: VfsPath;
  /** Present for `move`. */
  from?: VfsPath;
  entry?: FileEntry;
}

export interface PickOpts {
  /** Dialog title; the shell supplies a default when omitted. */
  title?: string;
  /** Directory the picker opens in. */
  startIn?: VfsPath;
  /** Filename suggested by a save picker. */
  suggestedName?: string;
  /** Accepted extensions, e.g. `['.txt', '.md']`. */
  extensions?: string[];
  /** Open pickers only. */
  multiple?: boolean;
  /** Pick a directory instead of a file. */
  directory?: boolean;
}

/** Client-side sync bookkeeping for one path (§7.4). */
export interface SyncEntry {
  rev: number;
  sha: string;
  dirty: boolean;
  /**
   * §7.8 — a **tombstone**: this path was deleted on this device and the server has not been told.
   *
   * It has to be recorded rather than inferred, because the evidence is gone. A push walks the
   * state map, and a deleted path has no bytes to hash and no file to stat — without a marker it
   * is indistinguishable from a path that was never touched, which is precisely how a deletion
   * fails to propagate. Optional so a state file written before this existed still parses.
   */
  deleted?: boolean;
  /**
   * D-194 — this path is a **directory**.
   *
   * Recorded for the same reason `deleted` is: a push walks the state map and decides what to do
   * from the entry alone. A directory has no bytes, so `files.read` would throw and take every
   * other pending change in that run down with it — and `sha` is empty for a folder, which is
   * indistinguishable from a file whose hash has not been computed yet. Optional so a state file
   * written before this existed still parses.
   */
  dir?: boolean;
}

// ── §7.1 path grammar ──────────────────────────────────────────────────────────────────────
//
// The grammar lives in `shared` because §19.3 requires the server to apply the *same* rules —
// "the client's confinement is not trusted". One implementation, two callers.

export const PATH_ERRORS = [
  'NOT_ABSOLUTE',
  'EMPTY_SEGMENT',
  'RELATIVE_SEGMENT',
  'NUL_BYTE',
  'TRAILING_SLASH',
  'SEGMENT_TOO_LONG',
  'PATH_TOO_LONG',
  'BACKSLASH',
] as const;
export type PathError = (typeof PATH_ERRORS)[number];

export type PathResult =
  | { ok: true; path: VfsPath; segments: string[] }
  | { ok: false; error: PathError };

const encoder = new TextEncoder();

export function byteLength(value: string): number {
  return encoder.encode(value).length;
}

/**
 * Validate and normalise a path against §7.1. Absolute, POSIX, NFC-normalised, no `.`/`..`, no
 * NUL, no trailing slash except for the root itself.
 *
 * Normalisation is deliberately *not* a resolver: `..` is rejected, never collapsed. Collapsing
 * is how a confinement check gets fooled, because the string that was checked stops being the
 * string that is used.
 */
export function normalizeVfsPath(input: string): PathResult {
  if (typeof input !== 'string' || input.length === 0 || input[0] !== '/') {
    return { ok: false, error: 'NOT_ABSOLUTE' };
  }
  if (input.includes('\0')) return { ok: false, error: 'NUL_BYTE' };
  if (input.includes('\\')) return { ok: false, error: 'BACKSLASH' };
  if (input === '/') return { ok: true, path: '/', segments: [] };
  if (input.endsWith('/')) return { ok: false, error: 'TRAILING_SLASH' };

  const normalized = input.normalize('NFC');
  const segments = normalized.slice(1).split('/');

  for (const segment of segments) {
    if (segment.length === 0) return { ok: false, error: 'EMPTY_SEGMENT' };
    if (segment === '.' || segment === '..') return { ok: false, error: 'RELATIVE_SEGMENT' };
    if (byteLength(segment) > MAX_SEGMENT_BYTES) return { ok: false, error: 'SEGMENT_TOO_LONG' };
  }

  const path = `/${segments.join('/')}`;
  if (byteLength(path) > MAX_PATH_BYTES) return { ok: false, error: 'PATH_TOO_LONG' };

  return { ok: true, path, segments };
}

/**
 * Is `path` inside `root` (or equal to it)? The separator is part of the comparison, so
 * `/Apps/ab` is **not** inside `/Apps/a` — the single most likely way to get this wrong.
 * Both arguments must already be normalised.
 */
export function isUnder(path: VfsPath, root: VfsPath): boolean {
  if (root === '/') return true;
  return path === root || path.startsWith(`${root}/`);
}

export function joinVfs(base: VfsPath, ...parts: string[]): VfsPath {
  const prefix = base === '/' ? '' : base;
  const tail = parts.filter((p) => p.length > 0).join('/');
  return tail.length === 0 ? base : `${prefix}/${tail}`;
}

export function parentOf(path: VfsPath): VfsPath {
  if (path === '/') return '/';
  const cut = path.lastIndexOf('/');
  return cut <= 0 ? '/' : path.slice(0, cut);
}

export function basename(path: VfsPath): string {
  return path === '/' ? '/' : path.slice(path.lastIndexOf('/') + 1);
}

/** The lowercased extension including the dot, or `''`. A leading dot is a name, not an ext. */
export function extname(path: VfsPath): string {
  const name = basename(path);
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot).toLowerCase();
}

/**
 * `report.tar.gz` splits at the last dot only; `.bashrc` is a name, not an extension (§7.1).
 * Here rather than in a caller because three of them need it — the file manager's paste, the
 * host-OS import (§7.7) and the server's §7.4 conflict copies — and one rule is the point.
 */
export function splitName(name: string): { stem: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { stem: name, ext: '' };
  return { stem: name.slice(0, dot), ext: name.slice(dot) };
}

/**
 * A name nothing in `taken` already uses: `notes.txt` → `notes (2).txt`. D-045's rule — nothing
 * is ever silently overwritten — is why every one of those callers needs it.
 */
export function uniqueName(taken: Iterable<string>, desired: string): string {
  const used = new Set(taken);
  if (!used.has(desired)) return desired;
  const { stem, ext } = splitName(desired);
  for (let n = 2; ; n += 1) {
    const candidate = `${stem} (${n})${ext}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** `/Apps/<appId>` — an app's private space, which needs no permission (§9.5). */
export function appRoot(appId: string): VfsPath {
  return `${ROOT_APPS}/${appId}`;
}

/** `/Users/<user>` — `guest` for an anonymous session (§7.1). */
export function userRoot(user: string): VfsPath {
  return `${ROOT_USERS}/${user}`;
}

export function trashRoot(user: string): VfsPath {
  return `${userRoot(user)}/${TRASH_DIR}`;
}
